import { dateSortKey } from "./format";

/**
 * Client-side filter + sort engine for the results table.
 *
 * The server returns up to 100 register records in one response, so all of
 * this runs in the browser: filtering is instant, nothing re-queries EUIPO,
 * and the user can narrow a result set without spending another API call.
 * If the page size ever grows past a few thousand rows, move `applyFilters`
 * behind a server endpoint — the shape of `filters` is designed to survive
 * that move unchanged.
 */

export const EMPTY_FILTERS = {
  wordMark: "",
  applicationNumber: "",
  applicant: "",
  jurisdiction: "",
  status: "",
  niceClass: "",
  filedFrom: "",
  filedTo: "",
  minSimilarity: "",
};

/** Text filters are substring, case-insensitive, whitespace-tolerant. */
function matchesText(value, needle) {
  if (!needle) return true;
  return String(value || "")
    .toLowerCase()
    .includes(needle.trim().toLowerCase());
}

function matchesDateRange(value, from, to) {
  if (!from && !to) return true;
  const time = dateSortKey(value);
  if (time === -Infinity) return false;
  if (from && time < new Date(from).getTime()) return false;
  if (to) {
    // Inclusive of the whole "to" day.
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    if (time > end.getTime()) return false;
  }
  return true;
}

export function applyFilters(results, filters, similarityMap) {
  if (!results?.length) return [];

  return results.filter((row) => {
    if (!matchesText(row.wordMark, filters.wordMark)) return false;
    if (!matchesText(row.applicationNumber, filters.applicationNumber))
      return false;
    if (!matchesText(row.applicantName, filters.applicant)) return false;
    if (filters.jurisdiction && row.jurisdiction !== filters.jurisdiction)
      return false;
    if (filters.status && row.status !== filters.status) return false;
    if (
      filters.niceClass &&
      !(row.niceClasses || []).includes(filters.niceClass)
    )
      return false;
    if (!matchesDateRange(row.filingDate, filters.filedFrom, filters.filedTo))
      return false;

    if (filters.minSimilarity) {
      const score = similarityMap?.get(row.trademarkId);
      if (score === undefined || score < Number(filters.minSimilarity))
        return false;
    }

    return true;
  });
}

/** Value used for sorting a given column. */
function sortValue(row, key, similarityMap) {
  switch (key) {
    case "wordMark":
      return (row.wordMark || "").toLowerCase();
    case "applicant":
      return (row.applicantName || "").toLowerCase();
    case "applicationNumber":
      return (row.applicationNumber || "").toLowerCase();
    case "jurisdiction":
      return (row.jurisdiction || "").toLowerCase();
    case "status":
      return (row.status || "").toLowerCase();
    case "niceClasses":
      return Number((row.niceClasses || [])[0]) || Infinity;
    case "filingDate":
      return dateSortKey(row.filingDate);
    case "similarity":
      return similarityMap?.get(row.trademarkId) ?? -1;
    default:
      return 0;
  }
}

export function applySort(rows, sort, similarityMap) {
  if (!sort?.key) return rows;

  const direction = sort.direction === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const left = sortValue(a, sort.key, similarityMap);
    const right = sortValue(b, sort.key, similarityMap);
    if (left < right) return -1 * direction;
    if (left > right) return 1 * direction;
    return 0;
  });
}

/**
 * Distinct values for the dropdown filters, derived from the result set so
 * the options only ever contain things that are actually present.
 */
export function deriveFilterOptions(results = []) {
  const jurisdictions = new Set();
  const statuses = new Set();
  const niceClasses = new Set();

  results.forEach((row) => {
    if (row.jurisdiction) jurisdictions.add(row.jurisdiction);
    if (row.status) statuses.add(row.status);
    (row.niceClasses || []).forEach((c) => niceClasses.add(String(c)));
  });

  return {
    jurisdictions: [...jurisdictions].sort(),
    statuses: [...statuses].sort(),
    niceClasses: [...niceClasses].sort((a, b) => Number(a) - Number(b)),
  };
}

export function countActiveFilters(filters) {
  return Object.entries(filters).filter(
    ([, value]) => value !== "" && value !== null,
  ).length;
}
